<script setup lang="ts">
import { formatInstantDate } from '@taler/domain'
import {
  ErrorState,
  EmptyState,
  IconButton,
  MenuItem,
  MenuPopover,
  NavIcon,
  Select,
  Skeleton,
} from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import ContactAvatar from '@/features/contacts/components/ContactAvatar.vue'
import { CONTACT_SORTS, type ContactSort, visibleContacts } from '../internal/list.ts'
import type { Contact } from '../internal/types.ts'

const props = defineProps<{
  contacts: readonly Contact[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  canForgive?: boolean
  canDelete?: boolean
}>()
const emit = defineEmits<{ retry: []; forgive: [contact: Contact]; delete: [contact: Contact] }>()

const { locale, t } = useI18n()
const loc = computed(() => locale.value as 'en' | 'hu')
const query = ref('')
const sort = ref<ContactSort>('name-asc')
const sortOptions = computed(() =>
  CONTACT_SORTS.map((value) => ({ value, label: t(`contacts.sort.${value}`) })),
)
const shown = computed(() => visibleContacts(props.contacts, query.value, sort.value, loc.value))
</script>

<template>
  <div class="flex flex-col gap-[var(--space-cell)]">
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
      <li v-for="index in 4" :key="index" class="flex items-center gap-3">
        <Skeleton width="40%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('contacts.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('contacts.errorTitle')"
      :body="t('contacts.errorBody')"
      :retry-label="t('contacts.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="contacts.length === 0"
      :title="t('contacts.emptyTitle')"
      :body="t('contacts.emptyBody')"
    >
      <template #icon><NavIcon name="contacts" /></template>
    </EmptyState>

    <template v-else>
      <div class="flex flex-wrap items-center gap-[var(--space-cell)]">
        <label
          class="flex h-[var(--density-control)] w-80 max-w-full items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text-muted)] focus-within:border-[color:var(--color-focus)] focus-within:outline focus-within:outline-[length:var(--ring-focus-width)] focus-within:outline-offset-[length:var(--ring-focus-offset)] focus-within:outline-[color:var(--color-focus)]"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            class="shrink-0"
          >
            <circle cx="7" cy="7" r="4.4" />
            <path d="M10.2 10.2 13.6 13.6" />
          </svg>
          <input
            v-model="query"
            type="search"
            :aria-label="t('contacts.searchLabel')"
            :placeholder="t('contacts.searchPlaceholder')"
            class="h-full min-w-0 flex-1 bg-transparent text-[length:var(--text-step-2)] text-[color:var(--color-text)] outline-none placeholder:text-[color:var(--color-text-muted)]"
          />
        </label>
        <Select v-model="sort" :options="sortOptions" :aria-label="t('contacts.sortLabel')" />
      </div>

      <p v-if="shown.length === 0" class="text-[color:var(--color-text-muted)]" role="status">
        {{ t('contacts.noMatches') }}
      </p>

      <table
        v-else
        class="w-full border-collapse rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
      >
        <thead>
          <tr class="table-head">
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('contacts.name') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('contacts.since') }}
            </th>
            <th class="w-12 px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              <span class="sr-only">{{ t('contacts.rowActionsHeading') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="contact in shown"
            :key="contact.id"
            class="table-row border-b border-[color:var(--color-border)] last:border-b-0"
          >
            <td class="h-[var(--density-row)] px-[var(--space-cell)] align-middle">
              <div class="flex items-center gap-3">
                <ContactAvatar :id="contact.id" :name="contact.name" />
                <div class="min-w-0">
                  <RouterLink
                    :to="{ name: 'contact-detail', params: { contactId: contact.id } }"
                    class="text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)] underline decoration-transparent underline-offset-4 transition-colors duration-[var(--motion-fast)] hover:decoration-current"
                  >
                    {{ contact.name }}
                  </RouterLink>
                  <p
                    v-if="contact.notes"
                    class="max-w-xs truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                  >
                    {{ contact.notes }}
                  </p>
                </div>
              </div>
            </td>
            <td
              class="h-[var(--density-row)] px-[var(--space-cell)] align-middle text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
            >
              {{ formatInstantDate(contact.createdAt, loc) }}
            </td>
            <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
              <MenuPopover side="bottom" align="end">
                <template #trigger>
                  <IconButton :aria-label="t('contacts.rowActions', { name: contact.name })">
                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                      <circle cx="3.5" cy="8" r="1.4" />
                      <circle cx="8" cy="8" r="1.4" />
                      <circle cx="12.5" cy="8" r="1.4" />
                    </svg>
                  </IconButton>
                </template>
                <MenuItem as-child>
                  <RouterLink :to="{ name: 'contact-detail', params: { contactId: contact.id } }">
                    {{ t('contacts.openAction') }}
                  </RouterLink>
                </MenuItem>
                <MenuItem as-child>
                  <RouterLink
                    :to="{ name: 'contact-detail', params: { contactId: contact.id }, query: { edit: '1' } }"
                  >
                    {{ t('contacts.editAction') }}
                  </RouterLink>
                </MenuItem>
                <MenuItem v-if="canForgive" @select="emit('forgive', contact)">
                  {{ t('contacts.forgiveAction') }}
                </MenuItem>
                <MenuItem v-if="canDelete" @select="emit('delete', contact)">
                  <span class="text-[color:var(--color-danger)]">{{ t('contacts.deleteAction') }}</span>
                </MenuItem>
              </MenuPopover>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>
